<template>
  <div ref="rootRef" class="dh-select" :class="{ 'is-open': open, 'is-disabled': disabled }">
    <div class="select-input" :title="displayText" @click="toggleOpen">
      <span v-if="displayText" class="select-label">{{ displayText }}</span>
      <span v-else class="select-placeholder">{{ placeholder }}</span>
      <span class="select-icons">
        <button
          v-if="clearable && modelValue !== undefined && modelValue !== null && modelValue !== ''"
          class="clear-btn"
          type="button"
          aria-label="Clear"
          @click.stop="clearValue"
        >×</button>
        <span class="caret" :class="{ 'up': open }">▾</span>
      </span>
    </div>

    <Transition name="dh-slide-down">
      <div
        v-if="open || closing"
        class="select-dropdown"
        :class="{ fading: dropdownFading }"
        role="listbox"
        :aria-activedescendant="activeId"
      >
        <ul
          ref="listRef"
          class="select-list"
          :class="{ 'dh-piano-in': open && !closing, 'dh-piano-out': closing }"
          :style="{ '--piano-base': pianoBase, '--piano-step': pianoStep }"
          tabindex="0"
          @keydown.prevent="onListKeydown"
        >
          <li
            v-for="(opt, idx) in options"
            :id="'dh-opt-' + idx"
            :key="String(opt.value) + '-' + idx"
            class="select-item"
            :class="{ selected: isSelected(opt), active: idx === activeIndex }"
            role="option"
            :aria-selected="isSelected(opt)"
            @click="selectOption(opt)"
            @mouseenter="activeIndex = idx"
          >
            {{ opt.label }}
          </li>
        </ul>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'

interface Option {
  label: string
  value: any
}

interface Props {
  /** 选中值 */
  modelValue?: any
  /** 选项列表 */
  options: Option[]
  /** 占位提示 */
  placeholder?: string
  /** 是否禁用 */
  disabled?: boolean
  /** 是否可清除 */
  clearable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  options: () => [],
  placeholder: '请选择',
  disabled: false,
  clearable: true
})

const emit = defineEmits<{
  /**
   * 更新选中值
   * @param value 新值
   */
  (e: 'update:modelValue', value: any): void
  /**
   * 值变更事件
   * @param value 新值
   */
  (e: 'change', value: any): void
}>()

const open = ref(false)
const closing = ref(false)
const dropdownFading = ref(false)
const pianoBase = '120ms'
const pianoStep = '70ms'
const activeIndex = ref(-1)
const rootRef = ref<HTMLElement | null>(null)
const listRef = ref<HTMLElement | null>(null)

const displayText = computed(() => {
  const found = props.options.find(o => isEqual(o.value, props.modelValue))
  return found?.label || ''
})

const activeId = computed(() => (activeIndex.value >= 0 ? `dh-opt-${activeIndex.value}` : undefined))

/**
 * 判断值相等
 * @param a 值A
 * @param b 值B
 * @returns 是否相等
 */
const isEqual = (a: any, b: any): boolean => {
  try {
    return JSON.stringify(a) === JSON.stringify(b)
  } catch {
    return a === b
  }
}

/**
 * 判断选项是否选中
 * @param opt 选项
 * @returns 是否选中
 */
const isSelected = (opt: Option): boolean => isEqual(opt.value, props.modelValue)

/**
 * 切换面板展开状态
 */
const toggleOpen = (): void => {
  if (props.disabled) return
  if (open.value) {
    requestClose()
  } else {
    open.value = true
    closing.value = false
    focusList()
  }
}

/**
 * 选中选项
 * @param opt 选项
 */
const selectOption = (opt: Option): void => {
  emit('update:modelValue', opt.value)
  emit('change', opt.value)
  requestClose()
}

/**
 * 清除选中值
 */
const clearValue = (): void => {
  emit('update:modelValue', '')
  emit('change', '')
}

/**
 * 聚焦列表并设置活动项
 */
const focusList = (): void => {
  if (!listRef.value) return
  const idx = props.options.findIndex(o => isSelected(o))
  activeIndex.value = idx >= 0 ? idx : 0
  listRef.value.focus()
}

/**
 * 列表键盘导航处理
 * @param e 键盘事件
 */
const onListKeydown = (e: KeyboardEvent): void => {
  if (!open.value) return
  const max = props.options.length - 1
  if (e.key === 'ArrowDown') {
    activeIndex.value = Math.min(activeIndex.value + 1, max)
  } else if (e.key === 'ArrowUp') {
    activeIndex.value = Math.max(activeIndex.value - 1, 0)
  } else if (e.key === 'Enter') {
    const opt = props.options[activeIndex.value]
    if (opt) selectOption(opt)
  } else if (e.key === 'Escape') {
    requestClose()
  }
}

/**
 * 外部点击收起面板
 * @param e 鼠标事件
 */
const onClickOutside = (e: MouseEvent): void => {
  if (!rootRef.value) return
  if (!rootRef.value.contains(e.target as Node)) {
    requestClose()
  }
}

onMounted(() => {
  document.addEventListener('click', onClickOutside, true)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onClickOutside, true)
})

// 同步活动项随值变化
watch(
  () => props.modelValue,
  () => {
    const idx = props.options.findIndex(o => isSelected(o))
    activeIndex.value = idx
  }
)

/**
 * 请求关闭下拉面板，播放钢琴键反向动画后再收起
 */
const requestClose = (): void => {
  if (!open.value || closing.value) return
  closing.value = true
  const itemStepMs = 60
  const itemDurationMs = 220
  const total = itemDurationMs + itemStepMs * Math.max(props.options.length - 1, 0) + 60
  const fadeStart = Math.max(total - 140, 80)
  window.setTimeout(() => {
    dropdownFading.value = true
  }, fadeStart)
  window.setTimeout(() => {
    open.value = false
    closing.value = false
    dropdownFading.value = false
  }, total)
}

</script>

<style scoped>
.dh-select {
  position: relative;
}

.select-input {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 32px;
  padding: 6px 10px;
  border-radius: 8px;
  background: var(--el-fill-color-blank);
  box-shadow: inset 0 0 0 1px var(--el-border-color-lighter);
  color: var(--el-text-color-primary);
  cursor: pointer;
}

.dh-select.is-disabled .select-input {
  cursor: not-allowed;
  opacity: 0.6;
}

.select-input:hover {
  box-shadow: inset 0 0 0 1px var(--el-border-color);
}

.dh-select.is-open .select-input {
  box-shadow: inset 0 0 0 1px var(--el-color-primary);
}

.select-label {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.select-placeholder {
  color: var(--el-text-color-placeholder);
}

.select-icons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.clear-btn {
  border: none;
  background: transparent;
  color: var(--el-text-color-secondary);
  cursor: pointer;
  font-size: 14px;
}

.clear-btn:hover {
  color: var(--el-text-color-primary);
}

.caret {
  font-size: 12px;
  transform: rotate(0deg);
  transition: transform 0.2s ease;
}

.caret.up {
  transform: rotate(180deg);
}

.select-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 2000;
  background: #fff;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 10px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  will-change: transform, opacity;
  transform: translateZ(0);
}

.select-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 240px;
  overflow-y: auto;
  overflow-x: hidden;
}

.select-item {
  padding: 10px 12px;
  color: var(--el-text-color-primary);
  cursor: pointer;
}

.select-item:hover,
.select-item.active {
  background: var(--el-color-primary-light-9);
  color: var(--el-color-primary);
}

.select-item.selected {
  background: var(--el-color-primary-light-8);
  color: var(--el-color-primary);
  font-weight: 600;
}

.select-dropdown.fading {
  opacity: 0;
  transition: opacity 0.14s ease;
}

</style>
